import { Box, useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { TimesheetRow } from '../../../types';
import { AvatarCell } from '../../../components/Table/CellRenderers/AvatarCell';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { Typography } from '../../../components/Typography/Typography';
import { EditableNumberCell } from '../components/SingleTimesheetTable/EditableNumberCell';
import TotalCell from '../components/SingleTimesheetTable/TotalCell';
import { TableHeaderCellWithClear } from '../components/SingleTimesheetTable/TableHeaderCellWithClear';
import { getTimesheetDateLabel } from '../../../utils/formatters';
import { useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../../state/UIState';
import { MonthTimesheetRowCheckbox } from '../components/SingleTimesheetTable/MonthTimesheetRowCheckbox';

const columnHelper = createColumnHelper<TimesheetRow>();

export type SingleTableColumnsOptions = {
  enableRowSelection?: boolean;
  readOnly?: boolean;
};

export const useSingleTableColumns = (
  weekStart: string,
  weekDates: string[],
  opts?: SingleTableColumnsOptions
) => {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const enableRowSelection = opts?.enableRowSelection ?? false;
  const readOnly = opts?.readOnly ?? false;

  const getDateLabel = useCallback(
    (dayOrder: number) => {
      return getTimesheetDateLabel(weekStart, dayOrder);
    },
    [weekStart]
  );

  const columns = useMemo(() => {
    const selectColumn = columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <MonthTimesheetRowCheckbox
          inHeader
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <MonthTimesheetRowCheckbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      enableSorting: false,
      footer: () => <Box sx={{ minWidth: 48 }} />,
      meta: {
        thStyles: { paddingLeft: '0', borderRight: 'none' },
        tdStyles: { paddingLeft: '0', borderRight: 'none' },
        width: '48px',
        maxWidth: '48px',
        minWidth: '48px',
      },
    });

    const dealColumn = columnHelper.accessor('name', {
      cell: (info) => {
        const isFixedDeal = info.row.original.dealId === 0;
        return (
          <AvatarCell
            name={info.row.original.name}
            avatarBoxStyle={{
              background: isFixedDeal
                ? colors.surfaceBackground.highlighted
                : colors.surfaceIndicator.needsReview,
            }}
            avatarLetterStyle={{
              color: colors.text.main,
            }}
          />
        );
      },
      enableSorting: false,

      header: () => <TableHeaderCell text='Deal' />,
      footer: () => {
        return (
          <Typography
            variant='body'
            color={colors.text.main}
            style={{
              paddingLeft: '30px',
              fontSize: '17px',
            }}
          >
            Total per day
          </Typography>
        );
      },
      meta: {
        tdStyles: { paddingLeft: isSmallScreen ? '8px' : '16px', borderRight: `none` },
        thStyles: { borderRight: `none`, paddingLeft: isSmallScreen ? '8px' : '16px' },
      },
    });

    const day = (dayOrder: number, accessor: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday') =>
      columnHelper.accessor(accessor, {
        cell: (info) => (
          <EditableNumberCell
            getValue={info.getValue}
            row={info.row}
            column={info.column}
            table={info.table}
            headerDate={getDateLabel(dayOrder)}
            weekDates={weekDates}
            readOnly={readOnly}
          />
        ),
        header: (row) => (
          <TableHeaderCellWithClear
            text={getDateLabel(dayOrder)}
            row={row}
            hideActions={readOnly}
          />
        ),
        footer: TotalCell,
        enableSorting: false,
        meta: {
          width: isSmallScreen ? '110px' : '16%',
          maxWidth: isSmallScreen ? '110px' : '16%',
          minWidth: isSmallScreen ? '110px' : '16%',
          tdStyles: {
            paddingLeft: '16px',
            borderRight: `none`,
            textAlign: 'right' as const,
            ...(accessor === 'friday'
              ? { paddingRight: '16px' as const }
              : {}),
          },
          thStyles: {
            borderRight: `none`,
            ...(accessor === 'friday' ? { paddingRight: '16px' as const } : {}),
          },
        },
      });

    const dayColumns = [
      day(1, 'monday'),
      day(2, 'tuesday'),
      day(3, 'wednesday'),
      day(4, 'thursday'),
      day(5, 'friday'),
    ];

    const base = [dealColumn, ...dayColumns];
    return enableRowSelection ? [selectColumn, ...base] : base;
  }, [
    colors.surfaceBackground.highlighted,
    colors.surfaceIndicator.needsReview,
    colors.text.main,
    enableRowSelection,
    getDateLabel,
    isSmallScreen,
    readOnly,
    weekDates,
  ]);

  return columns;
};
